import React, {
  forwardRef,
  cloneElement,
  isValidElement,
  LiHTMLAttributes,
  ReactElement,
  ReactNode,
  KeyboardEvent,
  useContext,
} from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import classNames from 'classnames';
import { useMenuContext } from './Menu';
import {
  menu_item_base_css,
  menu_group_item_css,
  menu_item_content_css,
  menu_item_icon_css,
  menu_item_label_css,
  menu_item_right_content_css,
  getMenuItemStyles,
  getMenuGroupStyles,
  getMenuSubItemStyles,
} from './styles';
import { IconProps } from '../icons/generated';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { semantic_colors, text_styles } from '../../foundation';
import { MenuGroupIdContext } from './context';

export interface MenuItemBaseProps extends Omit<LiHTMLAttributes<HTMLLIElement>, 'children' | 'onClick'> {
  id: string;
  /**
   * 메뉴 깊이에 따른 시각적 형태
   * @default 'default'
   */
  variant?: 'default' | 'sub';
  leftIcon?: ReactElement<IconProps>;
  rightContent?: ReactNode;
}

export interface MenuItemDefaultProps extends MenuItemBaseProps {
  asChild?: false;
  label: string;
  children?: never;
}

export interface MenuItemAsChildProps extends MenuItemBaseProps {
  /** 단일 ReactElement를 메뉴 아이템 루트로 합성합니다. */
  asChild: true;
  label?: never;
  children: ReactElement;
}

export type MenuItemProps = MenuItemDefaultProps | MenuItemAsChildProps;

export const MenuIcon = ({ selected, children }: { selected: boolean; children: ReactElement<IconProps> }) => {
  return isValidElement(children)
    ? cloneElement(children, {
        color: children.props.color ?? (selected ? semantic_colors.content.primary : semantic_colors.content.secondary),
        size: 16,
      })
    : null;
};

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  ({ className, id, label, leftIcon, rightContent, variant = 'default', asChild = false, children, ...props }, ref) => {
    const groupId = useContext(MenuGroupIdContext);
    const { activeMenu, onClickMenu, dense } = useMenuContext();
    const selected = activeMenu === id;
    const isSubItem = variant === 'sub';
    const color = isSubItem ? semantic_colors.content.secondary : semantic_colors.content.primary;
    const textKind = isSubItem
      ? selected
        ? 'Body_12_SemiBold'
        : 'Body_12_Regular'
      : groupId
      ? selected
        ? 'Body_13_SemiBold'
        : 'Body_13_Regular'
      : selected
      ? 'Body_13_SemiBold'
      : 'Body_13_Medium';
    const menuItemsStyles = isSubItem
      ? [menu_item_base_css, getMenuSubItemStyles(dense)]
      : groupId
      ? [menu_group_item_css, getMenuGroupStyles(dense)]
      : [menu_item_base_css, getMenuItemStyles(dense)];
    const handleSelect = () => onClickMenu({ id, groupId });
    const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
      // asChild 로 합성된 링크·버튼은 Enter/Space 의 기본 동작이 곧 클릭이므로,
      // 여기서 가로채면 이동이나 소비처 onClick 이 막힌다. 네이티브 동작에 맡긴다.
      if (asChild) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect();
      }
    };
    const Comp = asChild ? Slot : 'li';

    return (
      <Comp
        role='menuitem'
        tabIndex={0}
        className={classNames('pds-menu-item', className)}
        aria-current={selected ? 'page' : undefined}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
        css={[
          ...menuItemsStyles,
          asChild && text_styles[textKind],
          asChild && {
            color,
            '&:visited': { color },
          },
        ]}
        {...props}
        ref={ref}
      >
        {leftIcon && (
          <span css={menu_item_icon_css}>
            <MenuIcon selected={selected}>{leftIcon}</MenuIcon>
          </span>
        )}
        <Slottable>
          {asChild ? (
            isValidElement<{ children?: ReactNode }>(children) ? (
              cloneElement(
                children,
                undefined,
                <Stack direction='row' align='center' gap={0} css={menu_item_content_css}>
                  {children.props.children}
                  {rightContent && <div css={menu_item_right_content_css}>{rightContent}</div>}
                </Stack>,
              )
            ) : (
              children
            )
          ) : (
            <Stack direction='row' align='center' gap={0} css={menu_item_content_css}>
              <BaseText typography={textKind} color={color} css={menu_item_label_css}>
                {label}
              </BaseText>
              {rightContent && <div css={menu_item_right_content_css}>{rightContent}</div>}
            </Stack>
          )}
        </Slottable>
      </Comp>
    );
  },
);
